import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  Globe, 
  Smartphone, 
  ExternalLink,
  MessageSquare,
  Cpu,
  Loader2
} from 'lucide-react';
import { normalizeAISubmissionAnalysis, logStructuredError } from '@/lib/aiInsightsNormalizer';

interface FileDownloadLinkProps {
  filePath: string;
}

const FileDownloadLink = ({ filePath }: FileDownloadLinkProps) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleDownload = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('form-attachments')
        .createSignedUrl(filePath, 3600);

      if (error || !data?.signedUrl) {
        logStructuredError(error);
        toast({
          title: "Download Failed",
          description: "Could not generate download link for file.",
          variant: "destructive"
        });
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      logStructuredError(err);
      toast({
        title: "Download Error",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fileName = filePath.split('/').pop() || filePath;

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className="text-blue-500 hover:underline inline-flex items-center gap-1 text-left"
    >
      {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
      <span className="truncate max-w-[220px]">{fileName}</span>
    </button>
  );
};

interface SubmissionRow {
  id: string;
  form_id: string;
  data: Record<string, unknown>;
  status: string;
  created_at: string;
  ai_analysis?: unknown;
  payment_status?: string;
  completion_time?: number;
  user_agent?: string;
  ip_address?: string;
  device_type?: string;
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success';

const statusVariant = (s: string): BadgeVariant =>
  s === 'contacted' ? 'success' : s === 'cancelled' ? 'destructive' : s === 'completed' ? 'default' : 'secondary';

const formatDate = (d: string) =>
  new Date(d).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const extractContact = (data: Record<string, unknown>) => {
  let name = '';
  let phone = '';
  let email = '';
  for (const [k, v] of Object.entries(data || {})) {
    const key = k.toLowerCase();
    const val = typeof v === 'string' ? v : '';
    if (!name && (key.includes('name') || key === 'full_name')) name = val;
    if (!phone && (key.includes('phone') || key.includes('whatsapp') || key.includes('mobile'))) phone = val;
    if (!email && key.includes('email')) email = val;
  }
  return { name: name || 'Anonymous', phone, email };
};

const SubmissionDetail = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [submission, setSubmission] = useState<SubmissionRow | null>(null);
  const [formTitle, setFormTitle] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!submissionId || !user?.id) return;
      setLoading(true);
      try {
        const { data: sub, error } = await supabase
          .from('submissions')
          .select('*')
          .eq('id', submissionId)
          .maybeSingle();

        if (error || !sub) {
          logStructuredError(error);
          setSubmission(null);
          setLoading(false);
          return;
        }

        setSubmission(sub as unknown as SubmissionRow);
        const { data: form } = await supabase
          .from('forms')
          .select('title, user_id')
          .eq('id', sub.form_id)
          .maybeSingle();
        if (form) setFormTitle(form.title || '');
      } catch (err) {
        logStructuredError(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [submissionId, user?.id]);

  const markContacted = async () => {
    if (!submission) return;
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status: 'contacted' })
        .eq('id', submission.id);

      if (error) {
        logStructuredError(error);
        toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' });
        return;
      }
      setSubmission(prev => prev ? { ...prev, status: 'contacted' } : null);
      toast({ title: 'Updated', description: 'Submission marked as contacted.' });
    } catch (err) {
      logStructuredError(err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading submission details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!submission) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-12 text-center space-y-4">
          <h2 className="text-xl font-semibold">Submission Not Found</h2>
          <p className="text-sm text-muted-foreground">The requested submission does not exist or you do not have permission to view it.</p>
          <Button variant="outline" onClick={() => navigate('/forms')}>
            Back to Forms
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { name, phone, email } = extractContact(submission.data || {});
  const entries = Object.entries(submission.data || {});
  const humanLabels = entries.filter(([k]) => !k.startsWith('field_') && !/^[0-9a-f-]{36}$/i.test(k));
  const humanValues = new Set(humanLabels.map(([_, v]) => String(v)));
  const filtered = entries.filter(([k, v]) => {
    if ((k.startsWith('field_') || /^[0-9a-f-]{36}$/i.test(k)) && humanValues.has(String(v))) return false;
    if (k === 'form_id' || k === 'submission_id') return false;
    return true;
  });

  const ai = normalizeAISubmissionAnalysis(submission.ai_analysis);

  return (
    <DashboardLayout>
      <Helmet>
        <title>Submission · {formTitle || 'Bravax Forms'}</title>
      </Helmet>
      <div className="max-w-5xl mx-auto space-y-6 pb-16">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Submission Details</h1>
              <p className="text-sm text-muted-foreground">
                {formTitle ? (
                  <Link to={`/forms/${submission.form_id}/submissions`} className="hover:underline">
                    {formTitle}
                  </Link>
                ) : 'Form response'} · {formatDate(submission.created_at)}
              </p>
            </div>
          </div>
          <Badge variant={statusVariant(submission.status)} className="capitalize">
            {submission.status || 'pending'}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">Contact Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Respondent</Label>
                <p className="text-lg font-semibold">{name}</p>
              </div>
              {email && (
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm text-muted-foreground break-all">{email}</p>
                </div>
              )}
              {phone && (
                <div>
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <p className="text-sm text-muted-foreground">{phone}</p>
                </div>
              )}
              {phone && (
                <Button
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => {
                    const cleanPhone = phone.replace(/[^0-9]/g, '');
                    window.open(`https://wa.me/${cleanPhone}`, '_blank');
                  }}
                >
                  <MessageSquare className="h-4 w-4" /> WhatsApp Respondent
                </Button>
              )}
              {submission.status !== 'contacted' && (
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={markContacted}
                  disabled={updating}
                >
                  {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Mark as Contacted
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Metadata & Context</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Completion Time</p>
                  <p className="font-medium">{submission.completion_time ? `${submission.completion_time}s` : 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Device Type</p>
                  <p className="font-medium capitalize">{submission.device_type || 'Unknown'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">IP Address</p>
                  <p className="font-medium font-mono text-xs">{submission.ip_address || 'Hidden'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Form Field Responses</CardTitle>
            <CardDescription>All submitted data fields</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {filtered.length === 0 ? (
              <p className="text-muted-foreground text-center py-6">No form data available</p>
            ) : (
              filtered.map(([key, value]) => (
                <div key={key} className="p-4 rounded-lg bg-muted/40 border space-y-1">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{key}</Label>
                  <div className="text-sm font-medium text-foreground">
                    {typeof value === 'string' && value.startsWith('form-attachments/') ? (
                      <FileDownloadLink filePath={value} />
                    ) : typeof value === 'object' && value !== null ? (
                      <pre className="text-xs font-mono bg-background p-2 rounded overflow-x-auto">{JSON.stringify(value, null, 2)}</pre>
                    ) : (
                      String(value ?? '—')
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* AI Analysis */}
        {submission.ai_analysis && (
          <Card className="border-blue-500/20 bg-blue-500/5">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Cpu className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-lg">AI Analysis Insights</CardTitle>
              </div>
              <CardDescription>Automated processing of submission content</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Sentiment</Label>
                  <div>
                    <Badge variant={ai.sentiment === 'positive' ? 'success' : ai.sentiment === 'negative' ? 'destructive' : 'secondary'}>
                      {ai.sentiment.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Priority</Label>
                  <div>
                    <Badge variant={ai.priority === 'high' ? 'destructive' : 'outline'}>
                      {ai.priority.toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Intent</Label>
                  <p className="text-sm font-medium capitalize">{ai.intent}</p>
                </div>
              </div>
              {ai.summary && (
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">AI Summary</Label>
                  <p className="text-sm italic text-muted-foreground">"{ai.summary}"</p>
                </div>
              )}
              {ai.key_items?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Key Takeaways</Label>
                  <div className="flex flex-wrap gap-2">
                    {ai.key_items.map((item: string, i: number) => (
                      <Badge key={i} variant="outline" className="text-[10px]">{item}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SubmissionDetail;
